import { createRoot } from 'react-dom/client'
import './lib/tierSeedData' // load first – no deps, breaks potential circular imports
import './lib/firebase'
import './index.css'
import { reportErrorToAdmin, normalizeErrorForReporting, isFirestoreSdkNoise } from './lib/errorReporting'
import { isAuthNetworkError } from './lib/authErrorMessages'
import { ErrorBoundary } from './components/ErrorBoundary'
import { SubscriptionTiersProvider } from './contexts/SubscriptionTiersContext'
import { AuthProvider } from './contexts/AuthContext'
import { LanguageProvider } from './contexts/LanguageContext'
import App from './App.jsx'

// Known benign rejections: treat as low priority – device/browser issues (e.g. IndexedDB
// lost in Safari/Facebook in-app browser). Handle so app doesn't crash; do NOT report to admin.
function isKnownRecoverableRejection(message) {
  if (typeof message !== 'string') return false
  const s = message
  return (
    s.includes('Indexed Database server lost') ||
    s.includes('Connection to Indexed Database') ||
    s.includes('object is not extensible') ||
    s.includes('Cannot add property') ||
    isFirestoreSdkNoise(s)
  )
}

function isIndexedDbConnectionLost(message) {
  if (typeof message !== 'string') return false
  const s = message
  return s.includes('Indexed Database server lost') || s.includes('Connection to Indexed Database')
}

// Show a small banner asking the user to refresh (used for IndexedDB connection lost).
function showConnectionLostBanner() {
  if (typeof document === 'undefined' || document.getElementById('connection-lost-banner')) return
  const banner = document.createElement('div')
  banner.id = 'connection-lost-banner'
  banner.setAttribute('role', 'alert')
  banner.style.cssText = [
    'position:fixed;top:0;left:0;right:0;z-index:99999;',
    'display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap;',
    'padding:12px 20px;background:#1e3a5f;color:#fff;font-family:system-ui,sans-serif;font-size:14px;',
    'box-shadow:0 4px 12px rgba(0,0,0,0.15);',
  ].join('')
  banner.innerHTML = '<span>Connection was lost. Please refresh the page.</span><button type="button" style="padding:8px 16px;background:rgba(255,255,255,0.2);border:1px solid rgba(255,255,255,0.4);border-radius:8px;color:#fff;font-size:13px;font-weight:600;cursor:pointer;">Refresh</button>'
  const btn = banner.querySelector('button')
  if (btn) btn.addEventListener('click', () => window.location.reload())
  document.body.appendChild(banner)
}

// Global error handlers – report uncaught errors to admin; never pass raw/frozen errors through
if (typeof window !== 'undefined') {
  window.onerror = (message, source, lineno, colno, error) => {
    const src = typeof source === 'string' ? source : ''
    if (src && (src.indexOf('chrome-extension://') === 0 || src.indexOf('moz-extension://') === 0 || /popup\.js$/i.test(src))) {
      return false
    }
    const normalized = normalizeErrorForReporting(error || { message: String(message) })
    if (isKnownRecoverableRejection(normalized.message) || isFirestoreSdkNoise(String(message))) {
      return true
    }
    reportErrorToAdmin('window.onerror', normalized, {
      source: src,
      lineno: String(lineno ?? ''),
      colno: String(colno ?? ''),
    })
    return false
  }
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason instanceof Error ? event.reason : { message: String(event.reason), code: event.reason?.code }
    const normalized = normalizeErrorForReporting(reason)
    const isRecoverable = isKnownRecoverableRejection(normalized.message)
    const isAuthNetwork = isAuthNetworkError(reason)
    if (isAuthNetwork) {
      event.preventDefault()
      reportErrorToAdmin('unhandledrejection', normalized, {})
      try {
        window.dispatchEvent(new CustomEvent('connection-error', { detail: { code: normalized.code } }))
      } catch (_) {}
    } else if (isRecoverable) {
      event.preventDefault()
      // Don't report known device/browser issues – reduces noise in Error Report
      if (isIndexedDbConnectionLost(normalized.message)) {
        showConnectionLostBanner()
      }
    } else {
      reportErrorToAdmin('unhandledrejection', normalized, {})
    }
  })
}

/* Chrome/Android: scoped CSS fixes — idle so first paint stays unblocked. */
if (typeof document !== 'undefined' && typeof navigator !== 'undefined') {
  const applyPlatformClasses = () => {
    const ua = navigator.userAgent || ''
    if (/Android/i.test(ua)) document.documentElement.classList.add('ua-android')
    if (
      /iPhone|iPad|iPod/i.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ) {
      document.documentElement.classList.add('ua-ios')
    }
  }
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(applyPlatformClasses, { timeout: 2000 })
  } else {
    setTimeout(applyPlatformClasses, 0)
  }
}

const rootEl = document.getElementById('root')
if (rootEl) {
  if (
    typeof window !== 'undefined'
    && window.location.search
    && /[?&]id=/.test(window.location.search)
    && rootEl.hasAttribute('style')
  ) {
    rootEl.removeAttribute('style')
  }
  try {
    if (typeof createRoot !== 'function') {
      throw new Error('React createRoot is not available. Check that react-dom is loaded.')
    }
    createRoot(rootEl).render(
      <ErrorBoundary>
        <SubscriptionTiersProvider>
          <AuthProvider>
            <LanguageProvider>
              <App />
            </LanguageProvider>
          </AuthProvider>
        </SubscriptionTiersProvider>
      </ErrorBoundary>,
    )
  } catch (err) {
    console.error('[Bootstrap]', err)
    rootEl.innerHTML = `<div style="padding:24px;font-family:system-ui;background:#fef2f2;border:1px solid #fecaca;border-radius:12px;max-width:600px;margin:40px auto"><h2 style="color:#b91c1c">Bootstrap error</h2><pre style="overflow:auto;font-size:12px">${String(err?.message || err)}\n\n${err?.stack || ''}</pre></div>`
  }
}
